import LazyLoad from 'vanilla-lazyload';

import '../css/style.scss';
import '../css/tailwind.css';

import './ui/menu';
import './ui/legacyUiAdapter';
import './ui/modals';
import './ui/trustpilot';

import { mountReactComponent } from './react/mount';
import { getModal, MODAL_EVENTS } from './ui/modal';
import { persistUtmFromLocation } from './utmTracking';
import { domReady } from './ui/helpers';

try {
  persistUtmFromLocation();
} catch (error) {
  // eslint-disable-next-line no-console
  console.error('[utm] persist failed', error);
}

function importOnce(importer) {
  let promise;

  return () => {
    if (!promise) {
      promise = importer();
    }

    return promise;
  };
}

function loadWhenVisible(selector, load, rootMargin = '200px') {
  const targets = document.querySelectorAll(selector);
  if (!targets.length) {
    return;
  }

  if (!('IntersectionObserver' in window)) {
    load();
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      obs.disconnect();
      load();
    },
    {
      rootMargin,
    }
  );

  targets.forEach((target) => observer.observe(target));
}

const loadAnimation = importOnce(() =>
  import(/* webpackChunkName: "ui-animation" */ './ui/animation')
);
const loadArrowsHandler = importOnce(() =>
  import(/* webpackChunkName: "ui-arrows-handler" */ './ui/arrowsHandler')
);
const loadSliders = importOnce(() =>
  import(/* webpackChunkName: "ui-sliders" */ './ui/sliders')
);
const loadNeedsFilter = importOnce(() =>
  mountReactComponent(
    '#needs-filter',
    () => import(/* webpackChunkName: "needs-filter" */ './components/NeedsFilter')
  )
);
const loadFileSelector = importOnce(() =>
  mountReactComponent(
    '[id^="file-input-"]',
    () => import(/* webpackChunkName: "file-selector" */ './components/FileInput')
  )
);
const loadCallRequest = importOnce(() =>
  mountReactComponent(
    '#callRequestModal',
    () => import(/* webpackChunkName: "call-request" */ './components/CallRequest')
  )
);

domReady(() => {
  if (document.querySelector('[data-offer-banner], [data-marketing-offer-code]')) {
    import(/* webpackChunkName: "ui-marketing-offer" */ './ui/marketingOffer');
  }
});

if (document.querySelector('#marketing-offer-inline')) {
  mountReactComponent(
    '#marketing-offer-inline',
    () => import(/* webpackChunkName: "call-request" */ './components/CallRequest')
  );
}

if (
  document.querySelector("a[href*='#'][class*='anchor']:not([href='#'])") ||
  document.querySelector("iframe[src^='https://www.youtube.com'], .youtube-facade")
) {
  loadAnimation();
}

if (document.querySelector('.fat-arrow')) {
  loadArrowsHandler();
}

const setupSlidersLazyLoad = () => {
  if (!document.querySelector('.swiper-full-width')) {
    return false;
  }

  loadWhenVisible('.swiper-full-width', loadSliders, '300px');
  return true;
};

if (!setupSlidersLazyLoad()) {
  document.addEventListener('DOMContentLoaded', setupSlidersLazyLoad, { once: true });
  window.addEventListener('load', setupSlidersLazyLoad, { once: true });
}

if (document.querySelector('#needs-filter')) {
  loadWhenVisible('#needs-filter', loadNeedsFilter, '400px');
}

if (document.querySelector('[id^="file-input-"]')) {
  loadWhenVisible('[id^="file-input-"]', loadFileSelector, '300px');
}

if (document.querySelector('#callRequestModal')) {
  const callRequestTriggerSelector = '[data-ui-target="#callRequest"], a[href="#callRequest"]';
  const callRequestHash = '#callRequest';
  let isCallRequestReady = false;
  let isReplayingCallRequestClick = false;
  const logCallRequestLoadError = () => {
    // eslint-disable-next-line no-console
    console.error('[call-request] failed to load lazy chunk');
  };

  const ensureCallRequestReady = () => {
    if (isCallRequestReady) {
      return Promise.resolve();
    }

    return loadCallRequest().then(() => {
      isCallRequestReady = true;
    });
  };

  const openCallRequestModal = () => {
    const modal = document.getElementById('callRequest');
    if (!modal) {
      return;
    }

    // Ensure hash links are one-shot, like the generic modal hash handling.
    const clearHashOnHide = () => {
      if (window.location.hash === callRequestHash) {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      }
      modal.removeEventListener(MODAL_EVENTS.HIDDEN, clearHashOnHide);
    };

    modal.addEventListener(MODAL_EVENTS.HIDDEN, clearHashOnHide, false);
    getModal(modal)?.show();
  };

  const ensureCallRequestReadyFromHash = () => {
    if (window.location.hash !== callRequestHash) {
      return;
    }

    ensureCallRequestReady().then(openCallRequestModal).catch(logCallRequestLoadError);
  };

  ensureCallRequestReadyFromHash();
  window.addEventListener('hashchange', ensureCallRequestReadyFromHash, false);

  if (window.location.hash !== callRequestHash) {
    const warmUp = () => {
      ensureCallRequestReady().catch(logCallRequestLoadError);
    };

    window.addEventListener('scroll', warmUp, { once: true, passive: true });
    window.addEventListener('touchstart', warmUp, { once: true, passive: true });
    window.addEventListener('pointerdown', warmUp, { once: true, passive: true });
    window.addEventListener('keydown', warmUp, { once: true });
  }

  // Ensure the first click on "call request" opens with mounted React content.
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest(callRequestTriggerSelector);
      if (!trigger || isReplayingCallRequestClick || isCallRequestReady) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      ensureCallRequestReady()
        .then(() => {
          isReplayingCallRequestClick = true;
          trigger.dispatchEvent(
            new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
            })
          );
        })
        .catch(logCallRequestLoadError)
        .finally(() => {
          isReplayingCallRequestClick = false;
        });
    },
    true
  );
}

// eslint-disable-next-line no-new
new LazyLoad({
  elements_selector: '[data-src]',
});
